


















import { Vue, Component, Prop } from "vue-property-decorator";
import { GenericDevice } from "@/data/models/GenericDevice";
import firebase from "firebase";
import { formatDateShort, timestampToDate } from "@/utils/DateFormatter";
import { getNumberDaysBetween } from "@/utils/DateHelper";
import { GenericDeviceProjectAssignment, extractPowerConsumptionInput } from "@/data/models/GenericDeviceAssignment";
import { formatKWh } from "@/utils/LocalisationHelper";
import { roundWithPrecision, setLastVisitedItem, StorageKeyLastVisitedItemLevel } from "@/utils/Utils";
import DryingInfoTableComponent, { DryingInfoTable, DryingInfoTableRow } from "../devices/DryingInfoTable.vue";
import { COLOR_TEXT_HIGHLIGHT } from "@/utils/colors";

@Component({
  components: {
    "drying-info-table": DryingInfoTableComponent,
  },
})
export default class GenericDeviceHistoryItem extends Vue {
  @Prop() genericDevice!: GenericDevice;
  @Prop() previousProjectAssignment: GenericDeviceProjectAssignment | null;
  @Prop() genericDeviceProjectAssignment: GenericDeviceProjectAssignment;

  @Prop({ default: true }) showDivider!: boolean;
  @Prop() index!: number;
  @Prop() highlightEndValue!: number | null;
  @Prop() elevation!: number;

  @Prop({ default: "" })
  backgroundStyle!: string;

  @Prop({ default: false })
  forceUpdateBackgroundColor!: boolean;

  colorValues: string[] = [];
  tolerance = 1;

  get assignmentDurationInfo() {
    return this.getAssignmentDurationInfo(
      this.genericDeviceProjectAssignment.assignmentStartDate,
      this.genericDeviceProjectAssignment.assignmentEndDate
    ).toString()
  }

  get assignedProjectDescription(): string {
    const projectName = this.genericDeviceProjectAssignment.assignedProjectName;

    return this
      .$t("dryingInfo.project", [projectName])
      .toString();
  }

  get enableOpenProjectDetails() {
    return this.genericDeviceProjectAssignment.assignedProjectName && this.genericDeviceProjectAssignment.assignedProjectId;
  }

  get isActiveAssignment() {
    return !this.genericDeviceProjectAssignment.assignmentEndDate;
  }

  get meterReadingInfo(): string[] {
    const lastInput = this.previousProjectAssignment
      ?  extractPowerConsumptionInput(
          this.previousProjectAssignment.input
        )
      : null;
      
    const input = extractPowerConsumptionInput(
      this.genericDeviceProjectAssignment.input
    );

    const isExternalMeterUsed = this.genericDeviceProjectAssignment.externalElectricityMeterInfo;
    const externalMeterUsed = this.$t("deviceHistory.externalMeterUsed").toString();

    const lastInitialMeterReading = lastInput?.startValue ?? undefined;
    const lastLatestMeterReading = lastInput?.endValue ?? undefined;
    const initialMeterReading = input?.startValue ?? undefined;
    const latestMeterReading = input?.endValue ?? undefined;

    if (initialMeterReading === 0 && latestMeterReading === 0) {
      return isExternalMeterUsed ? [externalMeterUsed] : [""];
    }

    if (initialMeterReading === undefined) {
      return isExternalMeterUsed ? [externalMeterUsed] : [""];
    }

    if (latestMeterReading === undefined) {
      const meterReading = initialMeterReading !== 0
        ? [
            this.translateAndFormat(`dryingInfo.formatMeterReadingActiveProject`, initialMeterReading),
            ...isExternalMeterUsed ? [externalMeterUsed] : []
          ]
        : isExternalMeterUsed ? [externalMeterUsed] : [""];

      if (initialMeterReading !== 0) {
        if (
          lastLatestMeterReading &&
          Math.abs(initialMeterReading - lastLatestMeterReading) > 1
        ) {
          this.colorValues.push(COLOR_TEXT_HIGHLIGHT);
          this.$emit("onHighlightEndValue", this.index-1);
        }
        else if (!lastLatestMeterReading && lastInitialMeterReading) {
          this.colorValues.push(COLOR_TEXT_HIGHLIGHT);
        }
      }

      if (this.colorValues.length !== 0 && isExternalMeterUsed) {
        this.colorValues.push("");
      }

      return meterReading;
    }

    const powerConsumption = latestMeterReading - initialMeterReading;
    
    const isReadingDiffAcceptable = 
      (lastLatestMeterReading && Math.abs(initialMeterReading - lastLatestMeterReading) > 5) ||
      (!lastLatestMeterReading && lastInitialMeterReading);

    const isPowerConsumptionZero = powerConsumption === 0;

    if (isReadingDiffAcceptable) {
      this.colorValues.push(COLOR_TEXT_HIGHLIGHT);
      this.$emit("onHighlightEndValue", this.index - 1);

      this.addColorValues(0, isPowerConsumptionZero ? 1 : 2, this.highlightEndValue ? COLOR_TEXT_HIGHLIGHT : "");

      if (isPowerConsumptionZero) {
        this.colorValues.push(COLOR_TEXT_HIGHLIGHT);
      }
    } else if (this.highlightEndValue) {
      this.addColorValues(1, isPowerConsumptionZero ? 2 : 3, COLOR_TEXT_HIGHLIGHT);
      if (isPowerConsumptionZero) {
        this.colorValues.push(COLOR_TEXT_HIGHLIGHT);
      }
    } else if (isPowerConsumptionZero) {
      this.addColorValues(2, 3, COLOR_TEXT_HIGHLIGHT);
    }

    if (this.colorValues.length !== 0 && isExternalMeterUsed) {
      this.colorValues.push("");
    }

    const formattedInitialReading = this.translateAndFormat(`dryingInfo.formatMeterReadingStart`, initialMeterReading);
    const formattedLatestReading = this.translateAndFormat(`dryingInfo.formatMeterReadingEnd`, latestMeterReading);
    const formattedConsumption = this.translateAndFormat(`dryingInfo.formatMeterReadingConsumed`, powerConsumption);

    return [
      formattedInitialReading, 
      formattedLatestReading, 
      formattedConsumption,
      ...isExternalMeterUsed ? [externalMeterUsed] : []
    ];
  }

  addColorValues(startHighlightIndex: number, totalIterations: number, highlightColor: string): void {
    for (let i = 0; i < totalIterations; i++) {
      this.colorValues.push(i === startHighlightIndex ? highlightColor : "");
    }
  }

  translateAndFormat(key: string, value: number): string {
    return this.$t(key, [formatKWh(this, roundWithPrecision(value, 1), 1)]).toString();
  }

  onOpenProjectDetails() {
    const projectId = 
      this.genericDeviceProjectAssignment.assignedProjectId;

    setLastVisitedItem(
      this.genericDeviceProjectAssignment.id,
      StorageKeyLastVisitedItemLevel.LEVEL_4, 
    );
    
    this.$emit("openProjectDetails", projectId);
  }

  getAssignmentDurationInfo(
    startDate: firebase.firestore.Timestamp,  
    endDate: firebase.firestore.Timestamp | null,
  ) {
    const formattedStartDate = formatDateShort(startDate);
    if (endDate) {
      const startYear = timestampToDate(startDate)?.getFullYear();
      const endYear = timestampToDate(endDate)?.getFullYear();

      const formattedEndDate = formatDateShort(
        endDate,
        undefined,
        undefined,
        startYear !== endYear
      );
      const numDays = getNumberDaysBetween(
        startDate.toDate(),
        endDate.toDate()
      );
      const formattedNumDays = this
        .$tc("common.duration.formatDays", numDays, [numDays])
        .toString();

      return (
        this.$t("dryingInfo.formatDurationClosedAssignment", [
          formattedStartDate,
          formattedEndDate,
          formattedNumDays,
        ])
      ).toString();
    } else {
      const numDays = getNumberDaysBetween(startDate.toDate(), new Date());
      const formattedNumDays = this
        .$tc("common.duration.formatDays", numDays, [numDays])
        .toString();

      return (
        this.$t("dryingInfo.formatDurationOpenAssignment", [
          formattedStartDate,
          formattedNumDays,
        ])
      );
    }
  }

  get deviceHistoryInfoTable(): DryingInfoTable {
    const rows: DryingInfoTableRow[] = [];

    const addRow = (
      label: string, 
      values: string[], 
      bold?: boolean,
      colorLabel?: string,
      colorValues?: string[],
    ) => {
      if (values.length > 0) {
        rows.push({
          bold: bold,
          allowVariableValuesWidth: true,
          rowLabels: [label],
          rowValues: values,
          colorValues: colorValues ?? undefined,
        });
      }
    };

    addRow(
      this.$t("deviceHistory.headerLabel", [this.index]).toString(),
      [this.assignedProjectDescription] ?? [""],
      true
    );

    addRow(
      this.$t("dryingInfo.devicesAssinged").toString(),
      [this.assignmentDurationInfo]
    );

    const meterReading = this.meterReadingInfo;
    addRow(
      this.$t("panelDetail.meterReading").toString(),
      meterReading.length === 1 && meterReading[0] === ""
        ? []
        : meterReading,
      undefined,
      undefined,
      this.colorValues.length > 0 ? this.colorValues : undefined,
    );

    return {
      rows: rows,
    };
  }
}

